/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Col, Form, Input, Row, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

// misc imports
import useTheme from '../../../../utils/hooks/useTheme';
import { FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH } from '../../../../constants/deploymentFormPaths';

const BotDeploymentsZendeskStrategyGeneralForm = ({ form, hasEditAccess }) => {
  const { t } = useTranslation();
  const { formProps } = useTheme();

  return (
    <Fragment>
      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Row>
            <Col flex="auto">
              <Typography.Text>
                {t('Conversations API')}
              </Typography.Text>

              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Configure how Helvia reaches Zendesk\'s API')}
              </Typography.Paragraph>
            </Col>
          </Row>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            label={t('Zendesk Base URL')}
            name={[...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'baseUrl']}
            rules={[{
              required: true,
              message: t('Please enter the Zendesk Base url (without /sc)')
            }]}
          >
            {
              hasEditAccess
                ? <Input placeholder={t('Enter Zendesk Base url')} />
                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'baseUrl'])}</Typography.Text>
            }

          </Form.Item>
          <Form.Item
            label={t('Sunshine App ID')}
            name={[...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'appId']}
            rules={[{
              required: true,
              message: t('Please enter the Sunshine App ID')
            }]}
          >
            {
              hasEditAccess
                ? <Input placeholder={t('Enter Sunshine App ID')} />
                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'appId'])}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            label={t('Sunshine Conversations Key ID')}
            name={[...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'keyId']}
            rules={[{
              required: true,
              message: t('Please enter the Sunshine Conversations Key ID')
            }]}
          >
            {
              hasEditAccess
                ? <Input placeholder={t('Enter Sunshine Conversations Key ID')} />
                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'keyId'])}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            label={t('Sunshine Secret')}
            name={[...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'secret']}
            rules={[{
              required: true,
              message: t('Please enter the Sunshine Secret')
            }]}
          >
            <Input.Password
              size="large"
              placeholder={t('Sunshine Secret will not be visible while editing')}
              autoComplete="new-password" // Prevent autofill
              disabled={!hasEditAccess}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Row>
            <Col flex="auto">
              <Typography.Text>
                {t('Conversations Integrations')}
              </Typography.Text>

              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Configure how Zendesk reaches Helvia by a webhook')}
              </Typography.Paragraph>
            </Col>
          </Row>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            label={t('Sunshine Integration ID')}
            name={[...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'integrationId']}
            tooltip={{
              icon: <InfoCircleOutlined />,
              title:
              <Typography.Text type="secondary">
                {t('Zendesk-Sunshine conversations integration id. Apps and Integrations -> Conversations Integrations -> Your Integration -> Integration ID')}
              </Typography.Text>
            }}
            rules={[{
              required: true,
              message: t('Please enter the Sunshine Integration ID')
            }]}
          >
            {
              hasEditAccess
                ? <Input placeholder={t('Please enter the Sunshine Integration ID')} />
                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'integrationId'])}</Typography.Text>
            }
          </Form.Item>

          <Form.Item
            label={t('Webhook Id')}
            name={[...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'webhookId']}
            rules={[{
              required: true,
              message: t('Please enter the Webhook Id')
            }]}
          >
            {
              hasEditAccess
                ? <Input placeholder={t('Please enter the Webhook Id')} />
                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'webhookId'])}</Typography.Text>
            }
          </Form.Item>
          <Form.Item
            label={t('Webhook Key')}
            name={[...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'webhookKey']}
            rules={[{
              required: true,
              message: t('Please enter the Webhook Key')
            }]}
            tooltip={{
              title: (
                <Typography.Text type="secondary">
                  {
                    t('Go to Apps & Integrations -> Conversation Integrations and view details to show the Shared secret')
                  }
                </Typography.Text>
              ),
              icon: <InfoCircleOutlined />
            }}
          >
            {
              hasEditAccess
                ? <Input placeholder={t('Please enter the Webhook Key')} />
                : <Typography.Text>{form.getFieldValue([...FORM_CONFIGURATION_ZENDESK_INTEGRATION_PATH, 'webhookKey'])}</Typography.Text>
            }
          </Form.Item>
        </Col>
      </Row>
    </Fragment>

  );
};

BotDeploymentsZendeskStrategyGeneralForm.propTypes = {
  form: PropTypes.object,
  hasEditAccess: PropTypes.bool
};

export default BotDeploymentsZendeskStrategyGeneralForm;
